import { useEffect, useState, useRef } from "react";
import { Shield, ArrowRight, Clock, Zap, MessageSquareText } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import AuthModal from "@/components/AuthModal";
import IntelligenceTopBar from "@/components/IntelligenceTopBar";
import { useShieldCredits } from "@/hooks/useShieldCredits";
import { toast } from "sonner";
import AnalyzerInput from "@/components/AnalyzerInput";
import { useOnboarding } from "@/hooks/useOnboarding";
import AnalyzeGettingStarted from "@/components/onboarding/AnalyzeGettingStarted";
import QuizNudgeBanner from "@/components/QuizNudgeBanner";

interface RecentAnalysis {
  id: string;
  created_at: string;
  manipulation_primary_type: string | null;
  toxicity_score: number;
  grey_rock_response: string | null;
}

const FORENSIC_INSIGHTS = [
  {
    title: "Deflection: The 'I'm sorry you feel that way' Tactic",
    description:
      "This phrase shifts responsibility from the speaker's actions onto your emotions. It implies the problem is your reaction, not their behavior — a textbook deflection pattern.",
    pattern: "Deflection",
  },
  {
    title: "DARVO: Deny, Attack, Reverse Victim & Offender",
    description:
      "When confronted, the manipulator denies the behavior, attacks the person confronting them, and reverses the roles of victim and offender.",
    pattern: "DARVO",
  },
  {
    title: "The 'You Always' Escalation",
    description:
      "Using absolute language ('you always', 'you never') reframes a single event into a character flaw. It's designed to put you on the defensive.",
    pattern: "Escalation",
  },
];

const Overview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { refetch } = useShieldCredits();
  const [recentAnalyses, setRecentAnalyses] = useState<RecentAnalysis[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const onboarding = useOnboarding();

  // Auto-focus the Quick Analysis input when redirected with ?focus=analyze
  useEffect(() => {
    if (searchParams.get("focus") === "analyze" && textareaRef.current) {
      textareaRef.current.focus();
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("focus");
      setSearchParams(newParams, { replace: true });
    }
  }, []);

  // Random daily insight
  const [insight] = useState(() =>
    FORENSIC_INSIGHTS[Math.floor(Math.random() * FORENSIC_INSIGHTS.length)]
  );

  // Handle payment success/cancel redirects
  useEffect(() => {
    const payment = searchParams.get("payment");
    if (!payment) return;

    if (payment.endsWith("_success")) {
      toast.info("Checking your credit balance", {
        description: "Your balance updates after payment is confirmed.",
        duration: 6000,
      });
      refetch();
      const interval = setInterval(() => refetch(), 3000);
      setTimeout(() => clearInterval(interval), 15000);
    } else if (payment === "canceled") {
      toast.info("Payment was canceled.");
    }

    searchParams.delete("payment");
    setSearchParams(searchParams, { replace: true });
  }, []);

  // Fetch recent analyses — re-fetch on mount and when window regains focus
  const fetchRecent = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("neutralizations")
      .select("id, created_at, manipulation_primary_type, toxicity_score, grey_rock_response")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(5);
    setRecentAnalyses(data || []);
  };

  useEffect(() => {
    fetchRecent();
    const onFocus = () => fetchRecent();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user]);

  // handleAnalyze now lives inside AnalyzerInput component

  if (!user) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)" }}
      >
        <div className="rounded-xl border border-border bg-card p-8 text-center max-w-md">
          <Shield className="w-16 h-16 mx-auto mb-4 text-cyan opacity-60" />
          <h2 className="text-2xl font-bold mb-2">Welcome to GreyRock AI®</h2>
          <p className="text-muted-foreground mb-6">
            Sign in to access your Intelligence Console.
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className={cn(
              "px-6 py-3 rounded-xl font-semibold",
              "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
              "hover:scale-[1.02] transition-transform"
            )}
          >
            Get Started
          </button>
        </div>
        <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)" }}
    >
      <IntelligenceTopBar />

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8 space-y-6">
        {/* Page Title */}
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground">Behavioral Intelligence Dashboard</h1>

        {/* First-session nudge for quiz-referred signups (renders once, ever) */}
        <QuizNudgeBanner />

        {/* Getting Started Accordion */}
        <AnalyzeGettingStarted />

        {/* Return to Last Analysis Bar */}
        {sessionStorage.getItem("greyrock_last_analysis_msg") && (
          <button
            onClick={() => {
              const id = sessionStorage.getItem("greyrock_last_analysis_id");
              const msg = sessionStorage.getItem("greyrock_last_analysis_msg");
              navigate("/analysis-result", {
                state: id ? { analysisId: id } : { message: msg },
              });
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg border border-cyan/20 bg-cyan/5 hover:bg-cyan/10 transition-colors group"
          >
            <ArrowRight className="w-3.5 h-3.5 text-cyan rotate-180 shrink-0" />
            <span className="text-xs text-muted-foreground font-mono truncate flex-1 text-left">
              Current Analysis:{" "}
              <span className="text-foreground/80">
                "{sessionStorage.getItem("greyrock_last_analysis_msg")?.slice(0, 60)}
                {(sessionStorage.getItem("greyrock_last_analysis_msg")?.length ?? 0) > 60 ? "…" : ""}"
              </span>
            </span>
            <span className="text-xs font-semibold text-cyan shrink-0 group-hover:underline">
              ← Return to Full Report
            </span>
          </button>
        )}

        {/* Multi-Mode Analyzer */}
        <AnalyzerInput textareaRef={textareaRef} />

        {/* Two-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Today's Forensic Insight */}
          <div className="rounded-xl border border-border bg-card p-6 flex flex-col">
            <h2 className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-mono mb-4">
              Today's Forensic Insight
            </h2>
            <div className="flex-1 space-y-3">
              <h3 className="text-base font-semibold text-foreground leading-snug">
                {insight.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {insight.description}
              </p>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-cyan/10 text-cyan text-xs font-mono">
                <Shield className="w-3 h-3" />
                Pattern: {insight.pattern}
              </div>
            </div>
          </div>

          {/* Recent Analyses */}
          <div className="rounded-xl border border-border bg-card p-6 flex flex-col">
            <h2 className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-mono mb-4">
              Recent Analyses
            </h2>
            {recentAnalyses.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6">
                <Clock className="w-6 h-6 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No analyses yet</p>
                <p className="text-xs text-muted-foreground/60">Paste a message above to run your first analysis</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentAnalyses.map((a) => {
                  const date = new Date(a.created_at);
                  const scoreColor =
                    a.toxicity_score >= 8
                      ? "text-red-500"
                      : a.toxicity_score >= 5
                      ? "text-amber-400"
                      : "text-cyan";
                  const patternName = a.manipulation_primary_type
                    ? a.manipulation_primary_type.replace(/_/g, " ")
                    : "General";
                  return (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/analysis-result`, { state: { analysisId: a.id } })}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-secondary/50 border border-border hover:border-cyan/30 hover:bg-secondary/80 transition-all cursor-pointer w-full text-left"
                    >
                      <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground font-mono w-20 shrink-0">
                        {date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                      </span>
                      <span className="text-xs text-foreground/80 capitalize truncate flex-1">
                        {patternName}
                      </span>
                      {a.grey_rock_response && (
                        <span className="shrink-0" title="Response generated">
                          <MessageSquareText className="w-3.5 h-3.5 text-emerald-400" />
                        </span>
                      )}
                      <span className={cn("text-xs font-mono font-bold", scoreColor)}>
                        {a.toxicity_score}/10
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Overview;
